import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ndfApi } from '../../services/ndf-api';
import { Champ, Saisie, Selection } from './ui/Champ';
import { MessageErreur } from './ui/MessageEtat';

/**
 * Journal d'audit du module (R-HIS-02).
 *
 * Il répond à « qui a fait quoi, et quand » — la question qui se pose après
 * coup, quand un montant a changé ou qu'une note a disparu. Filtrable, parce
 * qu'un journal qu'on ne peut pas interroger n'est consulté par personne.
 */
export function JournalAudit() {
  const [filtres, setFiltres] = useState({ acteur: '', type: '', depuis: '', jusqua: '' });

  const { data, isLoading, error } = useQuery({
    queryKey: ['ndf', 'audit', filtres],
    queryFn: () => ndfApi.historique.audit(filtres),
    retry: false,
  });

  return (
    <div className="space-y-3">
      <section className="grid gap-3 rounded-lg border border-nuit/10 bg-white p-4 sm:grid-cols-4">
        <Champ label="Acteur">
          <Saisie
            value={filtres.acteur}
            placeholder="Une adresse, même partielle"
            onChange={(e) => setFiltres({ ...filtres, acteur: e.target.value })}
          />
        </Champ>
        <Champ label="Type d’événement">
          <Selection value={filtres.type} onChange={(e) => setFiltres({ ...filtres, type: e.target.value })}>
            <option value="">Tous</option>
            {(data?.types ?? []).map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </Selection>
        </Champ>
        <Champ label="Du">
          <Saisie type="date" value={filtres.depuis} onChange={(e) => setFiltres({ ...filtres, depuis: e.target.value })} />
        </Champ>
        <Champ label="Au">
          <Saisie type="date" value={filtres.jusqua} onChange={(e) => setFiltres({ ...filtres, jusqua: e.target.value })} />
        </Champ>
      </section>

      {isLoading && <p className="text-sm text-nuit/60">Chargement…</p>}
      {error && <MessageErreur erreur={error} />}

      {/* Une liste tronquée en silence laisse croire qu'on a tout vu. */}
      {data?.tronque && (
        <p className="rounded-md border border-jaune-fond/60 border-l-4 border-l-jaune-fond p-3 text-sm text-jaune">
          Les 500 événements les plus récents sont affichés. Affinez les filtres pour remonter plus loin.
        </p>
      )}

      <ul className="space-y-1">
        {(data?.evenements ?? []).map((e) => (
          <li key={e.id} className="rounded-md border border-nuit/10 bg-white p-2 text-sm">
            <div className="flex flex-wrap justify-between gap-2">
              <span className="font-medium text-nuit">{e.type}</span>
              <span className="text-nuit/60">
                {new Date(e.quand).toLocaleString('fr-FR')} : {e.qui}
              </span>
            </div>
            <p className="text-nuit/60">
              {e.entite_type} · {e.entite_id.slice(0, 8)}
              {e.details ? ` · ${JSON.stringify(e.details).slice(0, 120)}` : ''}
            </p>
          </li>
        ))}
      </ul>

      {data && data.evenements.length === 0 && (
        <p className="text-sm text-nuit/70">Aucun événement ne correspond à ces filtres.</p>
      )}
    </div>
  );
}
